<!-- Displays a patient's profile data as a labeled grid inside a chat message. -->

<script lang="ts" setup>
import { useTranslate } from '@nzyme/vue-i18n/useTranslate.js';

import type { PatientProfile } from '@sigma/common/patient/PatientProfile.js';

import PatientProfileDisplay from '../patients/PatientProfileDisplay.vue';
import * as l from './ChatProfileData.loc.js';

interface Props {
  /** The patient profile data to display. */
  profile: PatientProfile;
  /** Optional title override. Defaults to "Personal data" translation. */
  title?: string;
}

const props = defineProps<Props>();

const t = useTranslate();
</script>

<template>
  <div>
    <h3 :class="css.title">
      {{ props.title ?? t(l.title) }}
    </h3>

    <div :class="css.profileData">
      <PatientProfileDisplay
        v-slot="{ label, value }"
        :profile="profile"
      >
        <p :class="css.item">
          <span :class="css.itemLabel">{{ label }}</span>
          <span :class="css.itemValue">{{ value }}</span>
        </p>
      </PatientProfileDisplay>
    </div>
  </div>
</template>

<style lang="scss" module="css">
.title {
  font-size: $font-size-14;
  font-weight: $font-weight-600;
  margin: 0;
}

.profileData {
  display: flex;
  flex-wrap: wrap;
  gap: $spacing-08;
}

.item {
  display: flex;
  flex-direction: column;
  gap: $spacing-04;
  padding: $spacing-08;
  border-radius: $border-radius-08;
  background: $color-doctor-50;
  line-height: 1.2;
}

.itemLabel {
  font-size: $font-size-12;
  font-weight: $font-weight-500;
  color: $color-text-secondary;
  letter-spacing: $letter-spacing-02;
}

.itemValue {
  font-size: $font-size-16;
  font-weight: $font-weight-400;
  color: $color-text;
  line-height: 1.4;
  word-break: break-all;
}
</style>
